import { useState, useEffect } from "react";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import {
  getDefaultActivityPeriod,
  getDefaultContainerHistoryPeriod,
} from "../utils/activityPageFilters";

export { getDefaultContainerHistoryPeriod };

/**
 * Hook pour gérer les filtres d'activité (keyword, période, etc.)
 * et leur persistance dans localStorage
 */
export const useActivityFilters = ({
  storageKey = "activityFilters",
  getDefaultPeriod = getDefaultActivityPeriod,
  defaultPeriodNameKey = "Last 6 months",
} = {}) => {
  const location = useLocation();
  const { t } = useTranslation();
  const stateContainer = location.state || undefined;
  const displayCollecte =
    stateContainer !== undefined ? stateContainer.displayCollecte : false;
  const keywordF =
    stateContainer !== undefined ? stateContainer.keywordFilter : [];

  const normalizedKeywordF = Array.isArray(keywordF)
    ? { value: keywordF }
    : keywordF && keywordF.value
      ? keywordF
      : { value: [] };

  const [keywordFilter, setKeywordFilter] = useState(normalizedKeywordF);

  const [periodHistoryStart, setPeriodHistoryStart] = useState(
    () => getDefaultPeriod().periodHistoryStart,
  );
  const [periodHistoryStop, setPeriodHistoryStop] = useState(
    () => getDefaultPeriod().periodHistoryStop,
  );
  const [periodFilter, setPeriodFilter] = useState(
    () => getDefaultPeriod().periodFilter ?? null,
  );
  const [selectedPeriod, setSelectedPeriod] = useState("0");
  const [periodValue, setPeriodValue] = useState(null);
  const [formatedPeriod, setFormatedPeriod] = useState("");
  const [periodName, setPeriodName] = useState(() => t(defaultPeriodNameKey));
  const [hasSavedFilters, setHasSavedFilters] = useState(false);
  const [originFilter, setOriginFilter] = useState("all");

  // Shareable deep link (e.g. /activity?characterization=sent&characterizationStatus=Validated):
  // URL params win over the persisted filters below.
  const urlCharacterization = (() => {
    try {
      const value = new URLSearchParams(location.search || "").get(
        "characterization",
      );
      return value === "sent" || value === "not_sent" ? value : null;
    } catch {
      return null;
    }
  })();
  const urlCharacterizationStatus = (() => {
    try {
      const value = new URLSearchParams(location.search || "").get(
        "characterizationStatus",
      );
      return ["ImageEnTraitement", "ACompleter", "Validated"].includes(value)
        ? value
        : null;
    } catch {
      return null;
    }
  })();

  const [characterizationFilter, setCharacterizationFilter] = useState(
    () => urlCharacterization || "all",
  );
  const [characterizationStatusFilter, setCharacterizationStatusFilter] =
    useState(() => urlCharacterizationStatus || "all");

  useEffect(
    () => {
      const savedFilters = localStorage.getItem(storageKey);
      if (savedFilters) {
        try {
          const parsed = JSON.parse(savedFilters);
          setHasSavedFilters(true);

          const savedStart = parsed.periodHistoryStart
            ? new Date(parsed.periodHistoryStart)
            : null;
          const savedStop = parsed.periodHistoryStop
            ? new Date(parsed.periodHistoryStop)
            : null;

          const now = new Date();
          const today = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate(),
          );
          const savedStartOnly = savedStart
            ? new Date(
                savedStart.getFullYear(),
                savedStart.getMonth(),
                savedStart.getDate(),
              )
            : null;
          const savedStopOnly = savedStop
            ? new Date(
                savedStop.getFullYear(),
                savedStop.getMonth(),
                savedStop.getDate(),
              )
            : null;

          let nextStart = parsed.periodHistoryStart;
          let nextStop = parsed.periodHistoryStop;

          if (savedStartOnly && savedStopOnly) {
            if (savedStartOnly.getTime() === savedStopOnly.getTime()) {
              const normalizedStart = new Date(savedStartOnly);
              normalizedStart.setHours(0, 0, 0, 0);
              const normalizedStop = new Date(savedStopOnly);
              normalizedStop.setHours(23, 59, 59, 999);

              nextStart = normalizedStart.toISOString();
              nextStop = normalizedStop.toISOString();
            } else if (
              savedStartOnly.getTime() === today.getTime() &&
              savedStopOnly.getTime() === today.getTime()
            ) {
              const normalizedStart = new Date(today);
              normalizedStart.setHours(0, 0, 0, 0);
              const normalizedStop = new Date(today);
              normalizedStop.setHours(23, 59, 59, 999);

              nextStart = normalizedStart.toISOString();
              nextStop = normalizedStop.toISOString();
            }
          }

          if (nextStart) setPeriodHistoryStart(nextStart);
          if (nextStop) setPeriodHistoryStop(nextStop);

          if (parsed.periodValue) setPeriodValue(parsed.periodValue);
          if (parsed.selectedPeriod) setSelectedPeriod(parsed.selectedPeriod);
          if (
            parsed.periodFilter !== undefined &&
            parsed.periodFilter !== null
          ) {
            setPeriodFilter(parsed.periodFilter);
          }
          if (parsed.keywordFilter) {
            const normalized = Array.isArray(parsed.keywordFilter)
              ? { value: parsed.keywordFilter }
              : parsed.keywordFilter && parsed.keywordFilter.value
                ? parsed.keywordFilter
                : { value: [] };
            if (normalized.value && normalized.value.length > 0) {
              setKeywordFilter(normalized);
            } else {
              setKeywordFilter({ value: [] });
            }
          }
          if (parsed.formatedPeriod) setFormatedPeriod(parsed.formatedPeriod);
          if (parsed.periodName) setPeriodName(parsed.periodName);
          if (
            typeof parsed.originFilter === "string" &&
            parsed.originFilter.trim()
          ) {
            setOriginFilter(parsed.originFilter.trim());
          }
          // Characterization defaults to "all" (Tout) — do not restore
          // sent/not_sent from localStorage; only URL deep links override.
        } catch (error) {
          console.error(`Erreur lecture localStorage ${storageKey} :`, error);
          setHasSavedFilters(false);
        }
      } else {
        setHasSavedFilters(false);
        setPeriodName(t(defaultPeriodNameKey));
        setPeriodFilter(getDefaultPeriod().periodFilter);
      }
    }, // eslint-disable-next-line
    [],
  );

  useEffect(() => {
    const filtersToSave = {
      periodHistoryStart,
      periodHistoryStop,
      periodFilter,
      periodValue,
      selectedPeriod,
      keywordFilter,
      formatedPeriod,
      periodName,
      originFilter,
    };
    localStorage.setItem(storageKey, JSON.stringify(filtersToSave));
  }, [
    storageKey,
    periodHistoryStart,
    periodHistoryStop,
    periodFilter,
    periodValue,
    selectedPeriod,
    keywordFilter,
    formatedPeriod,
    periodName,
    originFilter,
  ]);

  return {
    keywordFilter,
    setKeywordFilter,
    periodHistoryStart,
    setPeriodHistoryStart,
    periodHistoryStop,
    setPeriodHistoryStop,
    periodFilter,
    setPeriodFilter,
    selectedPeriod,
    setSelectedPeriod,
    periodValue,
    setPeriodValue,
    formatedPeriod,
    setFormatedPeriod,
    periodName,
    setPeriodName,
    originFilter,
    setOriginFilter,
    characterizationFilter,
    setCharacterizationFilter,
    characterizationStatusFilter,
    setCharacterizationStatusFilter,
    displayCollecte,
    hasSavedFilters,
  };
};
